import type { GameInstanceConfig, GamePlugin, RoundContext } from '../types';
import { playerNetOnHole, stablefordPts } from '../helpers';
import { applyMoneyToNetOwed, appliesMoney, getLegStakes, getStakeConfig, pointsFromTotals } from '../stakes-helpers';
import { isPerHoleStake, perHoleStablefordSettlement } from '../stake-settlement';

export const stablefordPlugin: GamePlugin = {
  id: 'stableford',
  label: 'Stableford',
  description: 'Most points wins',
  requiresTeams: false,
  minPlayers: 2,
  defaultStakes: { scoringType: 'money', money: 10 },
  compute(ctx: RoundContext, config: GameInstanceConfig) {
    const stake = getStakeConfig(ctx.stakes, 'stableford');
    const keys = config.playerKeys?.length ? config.playerKeys : ctx.players.map((p) => p.playerKey);
    const rows = keys.map((key) => {
      const p = ctx.players.find((x) => x.playerKey === key);
      if (!p) return { playerKey: key, name: key, pts: 0 };
      let pts = 0;
      ctx.holes.forEach((h, i) => {
        const n = playerNetOnHole(p, i, ctx.holes, ctx.scores[key] || [], 'stroke', ctx.players);
        if (n == null) return;
        pts += stablefordPts(n - h.par);
      });
      return { playerKey: key, name: p.name, pts };
    }).sort((a, b) => b.pts - a.pts);

    const top = rows[0]?.pts ?? 0;
    const winners = rows.filter((r) => r.pts === top && top > 0);
    const totals: Record<string, number> = {};
    rows.forEach((r) => { totals[r.playerKey] = r.pts; });

    const raw: Record<string, number> = {};
    keys.forEach((k) => { raw[k] = 0; });

    if (appliesMoney(stake)) {
      if (isPerHoleStake(stake)) {
        const unit = (stake.moneyPerPoint ?? 1) * 100;
        Object.assign(raw, perHoleStablefordSettlement(ctx, unit, keys));
      } else {
        const rate = (stake.moneyPerPoint ?? 1) * 100;
        const topPts = rows[0]?.pts ?? 0;
        const winnerKeys = new Set(winners.map((w) => w.playerKey));
        const winnerCount = winnerKeys.size || 1;
        rows.forEach((r) => {
          if (winnerKeys.has(r.playerKey) && topPts > 0) {
            const fromLosers = rows
              .filter((l) => !winnerKeys.has(l.playerKey) && l.pts < topPts)
              .reduce((sum, l) => sum + (topPts - l.pts) * rate, 0);
            raw[r.playerKey] = Math.round(fromLosers / winnerCount);
          } else if (r.pts < topPts) {
            raw[r.playerKey] = -Math.round((topPts - r.pts) * rate);
          }
        });
      }
    }

    const me = ctx.players.find((p) => p.you);
    const meRow = me ? rows.find((r) => r.playerKey === me.playerKey) : null;
    const rank = me ? rows.findIndex((r) => r.playerKey === me.playerKey) + 1 : 0;
    const summary = meRow
      ? `${meRow.name} ${meRow.pts} pts · ${rank === 1 ? 'leading' : `#${rank}`}`
      : 'Stableford';

    return {
      formatId: 'stableford',
      label: 'Stableford',
      summary,
      standings: rows.map((r, i) => ({
        playerKey: r.playerKey,
        name: r.name,
        value: `${r.pts} pts`,
        rank: i + 1,
      })),
      netOwed: applyMoneyToNetOwed(raw, stake),
      pointsAwarded: pointsFromTotals(keys, totals, stake),
      winners: winners.map((w) => w.playerKey),
    };
  },
};

export const bestBallPlugin: GamePlugin = {
  id: 'best-ball',
  label: 'Best Ball',
  description: 'Team match — best net per hole',
  requiresTeams: true,
  minPlayers: 4,
  defaultStakes: { scoringType: 'money', money: 10 },
  compute(ctx: RoundContext, _config: GameInstanceConfig) {
    const stake = getStakeConfig(ctx.stakes, 'best-ball');
    const teams = ctx.teams;
    if (!teams || teams.length < 2) {
      return { formatId: 'best-ball', label: 'Best Ball', summary: 'Set up teams', netOwed: {} };
    }
    const legs = getLegStakes(stake);
    const teamNet = (keys: string[], hi: number) => {
      const vals = keys.map((key) => {
        const p = ctx.players.find((x) => x.playerKey === key);
        if (!p) return null;
        return playerNetOnHole(p, hi, ctx.holes, ctx.scores[key] || [], 'bestball', ctx.players);
      }).filter((v) => v != null) as number[];
      return vals.length ? Math.min(...vals) : null;
    };

    const seg = (lo: number, hi: number) => {
      let a = 0;
      let b = 0;
      for (let i = lo; i < hi; i++) {
        const x = teamNet(teams[0], i);
        const y = teamNet(teams[1], i);
        if (x == null || y == null) continue;
        if (x < y) a++;
        else if (y < x) b++;
      }
      return a - b;
    };
    const front = seg(0, 9);
    const back = seg(9, 18);
    const total = seg(0, 18);

    const raw: Record<string, number> = {};
    ctx.players.forEach((p) => { raw[p.playerKey] = 0; });

    const applyLeg = (diff: number, bet: number) => {
      if (diff === 0) return;
      const winTeam = diff > 0 ? 0 : 1;
      const loseTeam = diff > 0 ? 1 : 0;
      const owe = bet * 100;
      const winKeys = teams[winTeam];
      const loseKeys = teams[loseTeam];
      const winShare = Math.floor(owe / winKeys.length);
      const loseShare = Math.floor(owe / loseKeys.length);
      winKeys.forEach((k) => { raw[k] = (raw[k] || 0) + winShare; });
      loseKeys.forEach((k) => { raw[k] = (raw[k] || 0) - loseShare; });
    };
    applyLeg(front, legs.front);
    applyLeg(back, legs.back);
    applyLeg(total, legs.total);

    const summary = total === 0 ? 'All square' : total > 0 ? `Team A ${total} up` : `Team B ${-total} up`;
    return {
      formatId: 'best-ball',
      label: 'Best Ball',
      summary,
      netOwed: applyMoneyToNetOwed(raw, stake),
      winners: total > 0 ? teams[0] : total < 0 ? teams[1] : [],
      detail: { front, back, total },
    };
  },
};
